/**
 * useFeatureAccess — the single consumer-facing contract for resolving
 * what a user can see/reach, combining the build-time flag registry
 * with the runtime preview **mode** (a numbered delivery-phase ladder,
 * plus the non-numeric `"all"` radar mode).
 *
 * Resolution rules (2026-08-03 phase-ladder rewrite):
 *   - page released  = section flag AND page flag both on (hierarchy).
 *   - page visible   = released || revealedByMode(page).
 *   - section visible= mode-revealed-with-≥1-visible-page
 *                      OR (section flag on AND ≥1 released page).
 *   - route access   = page visible. Off + mode disallows → redirect.
 *
 * Mode semantics (delivery-phase ladder — supersedes the 2026-06-12 /
 * 2026-07-15 binary-tier rewrite):
 *   - mode 0 → no preview, released-only.
 *   - mode N (numbered phase, 1..MAX_PHASE) → reveals the union of
 *     phases `1..N` (see `PHASE_PAGE_IDS`), each not-yet-released.
 *     Cumulative: a higher phase always reveals at least as much as a
 *     lower one.
 *   - mode "all" → the flat "radar" view: every phase page id (any
 *     phase, not just ≤ the active one — there is no active phase
 *     number in this mode) PLUS every id in `RADAR_ONLY_PAGE_IDS`.
 *
 * This hook is the ONLY place call sites read access from. A future
 * swap to runtime/remote flags changes `activeFlags()` underneath
 * without touching any consumer.
 */
import { useMemo } from "react";
import {
  activeFlags,
  isPageReleased,
  isSectionReleased,
  resolveStage,
  type FeatureFlags,
} from "../config/featureFlags";
import {
  isNamedPreviewMode,
  type NamedPreviewMode,
  type PreviewMode,
  usePreview,
} from "./PreviewProvider";
import { LEFT_MENU_SECTIONS } from "../shell/leftMenuModel";

/**
 * Delivery-phase registry: which page ids ship in which numbered wave.
 * Phases are cumulative delivery waves, not a "reveal more" ladder —
 * "eventualmente iremos lanzando phase 1, y así sucesivamente" (Jose,
 * 2026-08-03). Adding a phase is a matter of adding another entry here
 * (paired with bumping `MAX_PHASE` in `PreviewProvider.tsx` and setting
 * `phase: N` on the relevant `leftMenuModel.ts` items) — every resolver
 * below is generic over `Object.keys/values(PHASE_PAGE_IDS)`, not a
 * hardcoded `1 | 2 | 3` union, so no other code needs to change.
 */
export const PHASE_PAGE_IDS: Record<number, ReadonlySet<string>> = {
  1: new Set(["tool-babychops-v2", "my-imports"]),
  // `dash-searches` + `dash-detail` graduated out of RADAR_ONLY_PAGE_IDS on
  // 2026-08-05 (epic `2026-08-05-my-searches-search-detail`): My Searches now
  // has a real page reading the Postgres mirror of Thrive's catalog, and
  // Search Detail is its sub-resource. Both moved from the Operations
  // placeholder to the Client Engagement section at the same time.
  //
  // `person-panel` joined on 2026-09-22 (Jose): the person side panel (epic
  // 2026-09-21-person-side-panel, sub-task 05). Like its phase-2 neighbours it
  // is now released on BOTH stages (prod delivery 2026-09-23), so this rung
  // reveals nothing for it any more — the entry stays as the record of when it
  // shipped. It is not a menu item (`NON_MENU_PHASE_PAGE_IDS`).
  2: new Set(["bct", "ai-review", "dash-searches", "dash-detail", "person-panel"]),
  // Phases 3 (`company-lists`, earlier `company-targets` and
  // `combine-filter`) and 4 (`tool-chopbook`) were DELETED on 2026-10-20, when
  // the ChopBook release shipped their last pages on every stage (Jose). Each
  // phase's last member emptied it, and an empty phase is worse than none:
  // `MAX_PHASE` is pinned to the highest key here (`leftMenuModel.test.ts`), so
  // a leftover empty set keeps `?preview=N` a valid mode that reveals nothing.
  // When a released id empties a phase: delete the key, lower `MAX_PHASE` and
  // the `PreviewMode` union in `PreviewProvider.tsx`, drop `phase: N` from the
  // nav item and from `LeftMenuItem.phase`, and re-date its "new" pill.
  //
  // Phases 5 (`bullseye`, the Bullseye tab) and 6 (`search-candidates`, the
  // Candidates tab) were opened on 2026-10-06 and DELETED on 2026-10-07, when
  // both tabs were released on every stage — each was its phase's only member,
  // so releasing it emptied the rung (the recipe next to phase 4). `MAX_PHASE`
  // went back to 4 with them, so `?preview=5` / `?preview=6` read as absent.
  // Unlike phases 1-2 they are not kept as a record: a rung nobody reaches
  // without a preview is a valid mode that reveals nothing.
};

/**
 * Phase page ids that are NOT sidebar items — a gated feature that lives inside
 * other pages and behind its own sub-resource route (`person-panel`: the avatar
 * button in Import Detail and `/people/in/:handle`), or inside another page's
 * route altogether (a tab of Search Detail, registered in
 * `IN_PAGE_FEATURE_IDS` in `routing/routes.tsx` — `bullseye` and
 * `search-candidates` were members until their release on 2026-10-07 took them
 * off the ladder). `leftMenuModel.test.ts` pins every OTHER phase id to a menu
 * item carrying the same `phase`; these are exempt from that check, and
 * asserted to have no menu item at all, so the exemption cannot hide a menu
 * entry that lost its `phase`.
 */
export const NON_MENU_PHASE_PAGE_IDS: ReadonlySet<string> = new Set(["person-panel"]);

/**
 * Unreleased page ids that NO preview mode may reveal on prod.
 *
 * The ladder reveals unreleased pages on BOTH stages by design: a recruiter's
 * `?preview=3` link showed Company Lists on prod until 2026-10-07 (Trello
 * #822). That is
 * right for a page that is ready to be shown to recruiters behind a link, and
 * wrong for one that is only ready for review on dev. An id here is revealed
 * by `?preview=N` / `?preview=all` on dev only; on prod it stays hidden until
 * its flag is released.
 *
 * Consulted by `pageRevealedByMode`, so the sidebar, `FeatureRoute`,
 * `pageVisible` and every tab gate get it from one place. A named preview
 * (`NAMED_PREVIEW_PAGE_IDS`) is checked first, so a named preview can still
 * reach a page listed here.
 *
 * Empty since 2026-10-20. Past members, each gone with its release:
 * `bullseye` (2026-10-06 → 2026-10-07), `company-lists` (2026-10-07, Trello
 * #822) and `tool-chopbook` (2026-10-07, Diego) → 2026-10-20 (the ChopBook
 * release). Kept as the mechanism for the next page that is ready for review
 * on dev but not for recruiters on prod.
 */
export const PROD_PREVIEW_BLOCKED_PAGE_IDS: ReadonlySet<string> = new Set<string>();

/**
 * The pages each named preview reveals, on dev and prod alike, and nothing
 * else: a named mode is not a rung, so it shows no phase's pages and makes no
 * section auto-reveal beyond these. Checked BEFORE
 * `PROD_PREVIEW_BLOCKED_PAGE_IDS`, so a named preview is the way to reach a
 * page the block hides from the ladder on prod.
 *
 * `chop-book` (`tool-chopbook`, `company-lists`) and `thrive-import`
 * (`thrive-search-import`) were deleted on 2026-10-20, and `four-reports`
 * (the in-page feature `four-reports` of the Candidates tab) on 2026-10-26,
 * each when their pages were released. A mode whose pages are all released is
 * a valid link that reveals nothing, so the release deletes the mode in the
 * same change.
 *
 * `staff-members` (2026-10-27): the Administration console pages; gated here
 * rather than via `?preview=all` so QA can reach them without the full radar.
 */
export const NAMED_PREVIEW_PAGE_IDS: Record<NamedPreviewMode, ReadonlySet<string>> = {
  "staff-members": new Set([
    "administration",
    "staff-members",
  ]),
};

/** Whether a named preview lists `pageId`. Widened by a cast so it
 *  still compiles when `NAMED_PREVIEW_MODES` is empty (`NamedPreviewMode` is
 *  then `never`). */
function namedPreviewReveals(mode: NamedPreviewMode, pageId: string): boolean {
  const pages = NAMED_PREVIEW_PAGE_IDS[mode] as ReadonlySet<string> | undefined;
  return pages?.has(pageId) ?? false;
}

/**
 * Ids with no phase yet — reachable ONLY via `?preview=all`. Includes
 * `sync-phone` (hidden 2026-06-18 per Jared feedback) per Jose's explicit
 * confirmation — see the PRD's "sync-phone" note in Context. The other
 * two are the unphased Operations placeholders (no `featureFlags`
 * entry, previously only reachable at the old tier 2).
 *
 * INVARIANT: disjoint from every `PHASE_PAGE_IDS` set. An id in both would
 * make `pageRevealedByMode` answer from whichever branch ran first, so the
 * same page would be revealed or hidden depending on the mode's SHAPE rather
 * than on the ladder. Asserted in `useFeatureAccess.test.tsx`.
 */
export const RADAR_ONLY_PAGE_IDS: ReadonlySet<string> = new Set([
  "dash-company",
  "our-tech",
  "sync-phone",
  // Hidden 2026-08-13 with the same treatment `sync-phone` got: the AgendaGPT
  // capability moved to a button on Search Detail, so the standalone form left
  // the sidebar — but the page still works and is worth reaching for internal
  // review. Without this line the flag flip alone would make
  // `/client-engagement/agenda` redirect to /dashboard under EVERY mode, which
  // is the retirement the PRD explicitly did not want (a retired route goes to
  // `LEGACY_REDIRECTS` instead).
  "agenda-gpt",
]);

/**
 * First page (in `leftMenuModel` order) of `sectionId` whose flag is
 * released under the given `flags`, or `null` when the section has none.
 *
 * Why this exists: the dashboard grid cards previously hard-coded one
 * `path` per section (e.g. `/research/imports` for Research & Sourcing,
 * `/operations/company-metrics` for Operations, `/closing/email-template`
 * for Closing). When the per-stage flag registry shifted under those
 * hardcodes (`my-imports` and `dash-company` are OFF on dev, and Closing's
 * canonical first page is `closing-email` → `/closing/email`, not the
 * Template Editor sub-row), the cards started dead-clicking through
 * `FeatureRoute` back to `/dashboard` or landing on the wrong page.
 * Resolving the href dynamically from the model + flags eliminates the
 * whole class of "card route drifted from feature flag" bugs.
 *
 * The leftMenuModel item order IS the canonical lifecycle order
 * ("Closing-email is the headline, Template Editor is its sub-row, so
 * Closing-email lists first"). Iterating in model order picks the right
 * primary page automatically — when flags shift, the card follows.
 *
 * Returns `null` when:
 *   - the section id is unknown, OR
 *   - the section is not released (`isSectionReleased` false), OR
 *   - no item in the section has its page flag on.
 * Callers render a `null` result as a disabled (greyed-out) card with
 * `aria-disabled`, never a "dead click" that redirects back to the
 * dashboard.
 *
 * Pure over `flags` — does NOT consult the preview mode. The dashboard
 * card grid intentionally routes to the first *real* released page (the
 * "what's actually shipped" anchor); preview reveals the off siblings via
 * the sidebar and the per-route `ComingSoonPage` instead.
 */
export function firstReleasedPageHref(
  sectionId: string,
  flags: FeatureFlags,
): string | null {
  if (!isSectionReleased(flags, sectionId)) return null;
  const section = LEFT_MENU_SECTIONS.find((s) => s.id === sectionId);
  if (!section) return null;
  const firstReleased = section.items.find((item) =>
    isPageReleased(flags, sectionId, item.id),
  );
  return firstReleased ? firstReleased.path : null;
}

export interface Access {
  /** Shipped on this stage (section + page flags both on). */
  released: boolean;
  /** Shown to this user (released, or visible under the active mode). */
  visible: boolean;
  /** Whether ANY preview mode is active (back-compat). */
  previewing: boolean;
  /** The active preview mode (0/1/2/3/4/"all"). 0 means no preview. */
  previewMode: PreviewMode;
}

export interface FeatureAccess {
  previewing: boolean;
  previewMode: PreviewMode;
  /** Access for a specific page within a section. */
  page: (sectionId: string, pageId: string) => Access;
  /** Access for a whole section (anti-empty-shell aware). */
  section: (sectionId: string) => Access;
}

/** Does the section have at least one released page under the flags? */
function sectionHasReleasedPage(
  flags: FeatureFlags,
  sectionId: string,
): boolean {
  if (!isSectionReleased(flags, sectionId)) return false;
  const section = LEFT_MENU_SECTIONS.find((s) => s.id === sectionId);
  if (!section) return false;
  return section.items.some(
    (item) =>
      item.id !== undefined && isPageReleased(flags, sectionId, item.id),
  );
}

/**
 * The delivery phase (1..MAX_PHASE) a page id belongs to, or `null` if it
 * isn't part of any numbered phase (e.g. a `RADAR_ONLY_PAGE_IDS` member,
 * or a page not on the roadmap at all).
 */
export function pageOwnPhase(pageId: string): number | null {
  for (const [phase, ids] of Object.entries(PHASE_PAGE_IDS)) {
    if (ids.has(pageId)) return Number(phase);
  }
  return null;
}

/**
 * Whether a given page id is revealed under the active preview mode.
 *   - mode 0 → never (preview off).
 *   - a named mode → true iff the id is listed for it in
 *     `NAMED_PREVIEW_PAGE_IDS`, on either stage.
 *   - on prod, an id in `PROD_PREVIEW_BLOCKED_PAGE_IDS` → never.
 *   - mode "all" → true iff the page belongs to any phase OR is in
 *     `RADAR_ONLY_PAGE_IDS` (the flat radar view).
 *   - mode N (numbered phase) → true iff the page's own phase is defined
 *     and `<= N` (cumulative reveal).
 */
export function pageRevealedByMode(
  mode: PreviewMode,
  pageId: string,
): boolean {
  if (mode === 0) return false;
  if (isNamedPreviewMode(mode)) return namedPreviewReveals(mode, pageId);
  if (resolveStage() === "prod" && PROD_PREVIEW_BLOCKED_PAGE_IDS.has(pageId)) return false;
  const ownPhase = pageOwnPhase(pageId);
  if (mode === "all") {
    return ownPhase !== null || RADAR_ONLY_PAGE_IDS.has(pageId);
  }
  return ownPhase !== null && ownPhase <= mode;
}

/**
 * Whether ANY page in the given section is revealed under the active
 * mode. Used by the section-visibility rule so a section auto-reveals
 * when it carries a mode-revealed page (no empty shells, even in
 * preview).
 *
 * `"all"` (radar) mirrors the old tier-2 special case: every section is
 * at least empty-reachable, so a structurally empty section (e.g.
 * Screening & Assessing — no items yet) still renders as an inert
 * "coming soon" header under the flat radar view, matching the
 * pre-existing UX where the full roadmap structure (including not-yet-
 * built stages) is visible for internal/product review.
 */
function sectionHasModeRevealedPage(
  mode: PreviewMode,
  sectionId: string,
): boolean {
  if (mode === 0) return false;
  const section = LEFT_MENU_SECTIONS.find((s) => s.id === sectionId);
  if (!section) return false;
  if (mode === "all") return true;
  return section.items.some((item) => pageRevealedByMode(mode, item.id));
}

/**
 * Whether an off, revealed page should show the "coming soon" (or
 * `nice-to-have`) balloon under the active mode. This is the phase-match
 * suppression rule: a phase's balloon disappears once the active mode
 * moves past that phase — the item is assumed to have matured past the
 * "brand-new preview" stage even though it hasn't been flipped
 * `released` yet.
 *
 *   - mode 0 → false (moot — the item wouldn't be revealed anyway).
 *   - mode "all" → true unconditionally (flat radar, no suppression).
 *   - mode N (numbered phase) → true iff the page's own phase === N
 *     (exact match only).
 *
 * Callers must still gate on `!released` first (a released item never
 * shows the balloon, unchanged rule) — this function only resolves the
 * mode-match half of that AND.
 */
export function shouldShowComingSoonBalloon(
  mode: PreviewMode,
  pageId: string,
): boolean {
  if (mode === 0) return false;
  if (mode === "all") return true;
  if (isNamedPreviewMode(mode)) return namedPreviewReveals(mode, pageId);
  return pageOwnPhase(pageId) === mode;
}

/**
 * Whether a page is visible under `flags` and `mode`: released, or revealed by
 * the preview ladder. The one resolution behind `useFeatureAccess().page()`,
 * exported for the gate that cannot assume a mounted `PreviewProvider` (see
 * `usePreviewModeIfMounted`).
 */
export function pageVisible(
  flags: FeatureFlags,
  mode: PreviewMode,
  sectionId: string,
  pageId: string,
): boolean {
  return (
    isPageReleased(flags, sectionId, pageId) || pageRevealedByMode(mode, pageId)
  );
}

export function useFeatureAccess(): FeatureAccess {
  const { previewing, previewMode } = usePreview();
  const flags = activeFlags();

  return useMemo<FeatureAccess>(() => {
    const page = (sectionId: string, pageId: string): Access => {
      const released = isPageReleased(flags, sectionId, pageId);
      return {
        released,
        visible: pageVisible(flags, previewMode, sectionId, pageId),
        previewing,
        previewMode,
      };
    };

    const section = (sectionId: string): Access => {
      const released = sectionHasReleasedPage(flags, sectionId);
      const revealedByMode = sectionHasModeRevealedPage(
        previewMode,
        sectionId,
      );
      return {
        released,
        visible: released || revealedByMode,
        previewing,
        previewMode,
      };
    };

    return { previewing, previewMode, page, section };
  }, [flags, previewing, previewMode]);
}
